import React from 'react';
import { Avatar } from '@components/atoms/Avatar';
import { StatsCard } from '@components/molecules/StatsCard';
import { SubcontractorOverview } from '@models/subcontractor.model';
import { useTranslation } from '@hooks/useTranslation';
import { formatDateOnly } from '@utils/formatDate';
import { formatMoney, t } from '../utils/format';
import { HealthPill } from './HealthPill';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PaidIcon from '@mui/icons-material/Paid';

interface SubcontractorHeaderProps {
  overview: SubcontractorOverview;
}

export const SubcontractorHeader: React.FC<SubcontractorHeaderProps> = ({ overview }) => {
  const tSub = useTranslation('subcontractors');
  const { header, totals, subOrganizationName } = overview;

  const relationshipLine = [
    header.since ? t(tSub?.since, 'Since {date}', { date: formatDateOnly(header.since) }) : null,
    t(tSub?.projectsCount, '{n} of my projects', { n: header.projectsCount }),
    t(tSub?.tasksBreakdown, '{n} tasks ({active} active · {complete} complete · {cancelled} cancelled)', {
      n: header.tasksTotal,
      active: header.tasksActive,
      complete: header.tasksComplete,
      cancelled: header.tasksCancelled,
    }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="px-8 flex-shrink-0">
      <div className="flex items-center gap-3 mb-1">
        <Avatar text={subOrganizationName} size="md" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{subOrganizationName}</h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{relationshipLine}</p>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">
          {tSub?.agreedPrice || 'Agreed price'}
        </span>
        <span className="text-lg font-bold text-gray-900 dark:text-white">
          {formatMoney(header.agreedPriceTotal, tSub?.notSet || 'Not set')}
        </span>
        {header.tasksWithoutPriceCount > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t(tSub?.withoutPrice, '{n} tasks without a price', { n: header.tasksWithoutPriceCount })}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatsCard
          icon={<HealthPill health={totals.health} />}
          value={`${Math.round(totals.completion * 100)}%`}
          label={t(tSub?.complete, '{pct}% complete', { pct: Math.round(totals.completion * 100) })}
        />
        <StatsCard
          icon={<ScheduleIcon className="text-red-500" />}
          value={totals.overdueCount}
          label={
            totals.avgDaysLate != null
              ? t(tSub?.avgDaysLate, 'avg {d} d late ({n})', {
                  d: totals.avgDaysLate,
                  n: totals.completedLateCount,
                })
              : t(tSub?.overdue, '{n} overdue', { n: totals.overdueCount })
          }
        />
        <StatsCard
          icon={<PaidIcon className="text-emerald-600" />}
          value={formatMoney(totals.earned, tSub?.notSet || 'Not set')}
          label={
            totals.ofAgreedPct != null
              ? t(tSub?.ofAgreed, '{pct}% of agreed', { pct: Math.round(totals.ofAgreedPct * 100) })
              : tSub?.earned || 'Earned to date'
          }
        />
        <StatsCard
          icon={<WarningAmberIcon className="text-amber-500" />}
          value={formatMoney(totals.atRisk, tSub?.notSet || 'Not set')}
          label={tSub?.onOverdue || 'on overdue tasks'}
        />
      </div>
    </div>
  );
};

export default SubcontractorHeader;
